import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import Config from '../../config';

import { Tabs, Tab } from '../../shared/tabs.component';
import Select from '../../shared/reactSelect/select.component';
import PeriodDropdown from '../../shared/periodDropdown.component';

import SecondaryMenu from '../../menu/secondaryMenu.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import {
  selectPeriodForBatchProvisions,
  selectConnectionForBatchProvisions,
  fetchBatchProvisions,
  fetchInitialDataForBatchProvisions,
  processBatchProvisions,
  processBatchProvisionsForGlUIConnection,
  processBatchProvisionsForFTPConnection,
  hideCannotBeginProcessingModal,
  showCannotBeginProcessingModal,
  refreshStatus,
} from './store/actions';
import {
  defaultColumnDefs,
  allCompaniesColumnDefs,
  processedSuccessfullyColumnDefs,
  trialBalancesNotReceivedColumnDefs,
  trialBalancesValidationErrorsColumnDefs,
  calculationErrorsColumnDefs,
  otherErrorsColumnDefs,
} from '../../shared/columnDefinitions/batchProvisionsPage';

import BatchProvisionJournalsPill from './batchProvisionJournalsPill.component';
import FileUIConnectionForm from './fileUIConnectionForm.component';
import GlUIConnectionForm from './glUIConnectionForm.component';

import { successNotification, errorNotification } from '../../shared/store/notificationActions';
import {
  periodOrCurrentSelector,
  periodsSelector,
  currentPeriodSelector,
  userRoleSelector,
  environmentSuffixSelector,
  isInCurrentPeriodSelector,
} from '../../shared/store/selectors';
import {
  connectionsOptionsSelector,
  connectionSelector,
  isGlUIConnectionSelectedSelector,
  hasUploadTrialBalancePrivilegeSelector,
} from '../store/selectors';
import { renderDate } from '../../shared/dateHelpers';
import { connectionsTypes, ModalNames } from '../constants';

import { ReduxAlert } from '../../shared/alert.component';

import CannotBeginProcessingMessageModal from '../cannotBeginProcessingMessageModal';
import { modalDataSelector } from '../../shared/modals/modals.selectors';
import HamburgerDropdown from './hamburgerDropdown.component';
import {
  companyCodesForConnectionSelector,
  isFetchingBatchProvisionsSelector,
  isLoadingCompanyCodesForConnectionSelector,
  isRefreshingStatusSelector,
  isStartingCalcBatchProvisionsSelector,
  canLoadTrialBalanceAndCalculateSelector,
} from './store/selectors';
import { connectionTemplatesDetailsPropType } from '../../administration/beginningBalancesPage/propTypes';
import JimJobInfo from '../../shared/jim/jimJobInfo.component';
import {
  isAddingJobOfTypeSelectorFactory,
  isLastJobOfTypeInProgressSelectorFactory,
} from '../../shared/jim/store/selectors';

import { fetchCustomReports } from '../../reports/customReports/store/actions';
import { batchProvisionJournalsReportAndReportSetIdsSelector } from '../../reports/customReports/store/selectors';
import { updateLocalStorageAndPushCustomReport } from '../../reports/customReports/generateBaseCustomReportLink';

const TabTypes = {
  ALL_COMPANIES: 'ALL_COMPANIES',
  PROCESSED_SUCCESSFULLY: 'PROCESSED_SUCCESSFULLY',
  TRIAL_BALANCES_NOT_RECEIVED: 'TRIAL_BALANCES_NOT_RECEIVED',
  TRIAL_BALANCES_WITH_ERRORS: 'TRIAL_BALANCES_WITH_ERRORS',
  CALCULATION_ERRORS: 'CALCULATION_ERRORS',
  OTHER_ERRORS: 'OTHER_ERRORS',
  JOURNALS: 'JOURNALS',
};

class BatchProvisionsPage extends PureComponent {
  static propTypes = {
    isUploading: PropTypes.bool.isRequired,
    isStartingCalc: PropTypes.bool.isRequired,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    currentPeriod: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    periods: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    connections: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        connType: PropTypes.string.isRequired,
      })
    ),
    connection: PropTypes.shape({
      connName: PropTypes.string.isRequired,
      connType: PropTypes.string.isRequired,
    }),
    userRole: PropTypes.string.isRequired,
    selectPeriodForBatchProvisions: PropTypes.func.isRequired,
    selectConnectionForBatchProvisions: PropTypes.func.isRequired,
    fetchBatchProvisions: PropTypes.func.isRequired,
    fetchInitialDataForBatchProvisions: PropTypes.func.isRequired,
    processBatchProvisions: PropTypes.func.isRequired,
    processBatchProvisionsForGlUIConnection: PropTypes.func.isRequired,
    processBatchProvisionsForFTPConnection: PropTypes.func.isRequired,
    status: PropTypes.shape({
      updatedOn: PropTypes.string,
      connStatusName: PropTypes.string,
      tbLoadStatus: PropTypes.number,
      tbBatchID: PropTypes.number,
    }),
    processedSuccessfully: PropTypes.arrayOf(
      PropTypes.shape({
        companyCode: PropTypes.string,
        ccDescription: PropTypes.string,
        legalEntity: PropTypes.string,
        countryName: PropTypes.string,
        statusName: PropTypes.string,
        updatedOn: PropTypes.string,
      })
    ),
    trialBalancesNotReceived: PropTypes.arrayOf(
      PropTypes.shape({
        companyCode: PropTypes.string,
        ccDescription: PropTypes.string,
        legalEntity: PropTypes.string,
        countryName: PropTypes.string,
        statusName: PropTypes.string,
      })
    ),
    trialBalancesWithErrors: PropTypes.arrayOf(
      PropTypes.shape({
        companyCode: PropTypes.string,
        ccDescription: PropTypes.string,
        legalEntity: PropTypes.string,
        countryName: PropTypes.string,
        importResults: PropTypes.string,
        importStatus: PropTypes.string,
      })
    ),
    calculationsWithErrors: PropTypes.arrayOf(
      PropTypes.shape({
        companyCode: PropTypes.string,
        ccDescription: PropTypes.string,
        legalEntity: PropTypes.string,
        countryName: PropTypes.string,
        importStatus: PropTypes.string,
      })
    ),
    otherErrors: PropTypes.arrayOf(
      PropTypes.shape({
        connectionName: PropTypes.string,
        companyCode: PropTypes.string,
        notes: PropTypes.string,
      })
    ),
    allProvisions: PropTypes.arrayOf(
      PropTypes.shape({
        companyCode: PropTypes.string,
        ccDescription: PropTypes.string,
        legalEntity: PropTypes.string,
        countryName: PropTypes.string,
        statusName: PropTypes.string,
        updatedOn: PropTypes.string,
      })
    ),
    general: PropTypes.shape({
    }),
    componentToRenderOnSearchBar: PropTypes.node,
    isSelectedGlUIConnection: PropTypes.bool,

    hasUploadTrialBalancePrivilege: PropTypes.bool.isRequired,
    hideCannotBeginProcessingModal: PropTypes.func.isRequired,
    showCannotBeginProcessingModal: PropTypes.func.isRequired,
    cannotBeginProcessingMessageModal: PropTypes.shape({
      show: PropTypes.bool.isRequired,
    }).isRequired,

    companyCodesForConnection: connectionTemplatesDetailsPropType,
    environmentSuffix: PropTypes.string.isRequired,
    isLoadingCompanyCodesForConnection: PropTypes.bool.isRequired,

    fetchCustomReports: PropTypes.func.isRequired,
    batchProvisionJournalsReportIds: PropTypes.shape({
      reportId: PropTypes.string,
      reportSetId: PropTypes.string,
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    successNotification: PropTypes.func.isRequired,
    errorNotification: PropTypes.func.isRequired,
    isCreatingJob: PropTypes.bool,
    isFetchingBatchProvisions: PropTypes.bool,

    refreshStatus: PropTypes.func.isRequired,
    isRefreshingStatus: PropTypes.bool.isRequired,
    canLoadTrialBalanceAndCalculate: PropTypes.bool.isRequired,
    isInCurrentPeriod: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    status: {
      connStatusName: null,
      updatedOn: null,
      tbLoadStatus: null,
      tbBatchID: null,
    },
  };

  state = {
    activeTab: TabTypes.ALL_COMPANIES,
    isUploading: false,
    calculateProvisions: false,
    loadTrialBalances: false,
    proformaCalc: false,
    ledgers: '',
  };

  componentDidMount() {
    this.props.fetchInitialDataForBatchProvisions();
    this.props.fetchCustomReports();
    this.props.fetchBatchProvisions();
  }

  componentDidUpdate(prevProps) {
    const { connection: prevConnection } = prevProps;
    const { connection } = this.props;

    const previousConnectionType = prevConnection && prevConnection.connType;
    const currentConnectionType = connection && connection.connType;

    if (
      previousConnectionType !== connectionsTypes.GlUI
      && currentConnectionType === connectionsTypes.GlUI
    ) {
      return this.setState({
        calculateProvisions: true,
        loadTrialBalances: true,
        proformaCalc: false,
      });
    }
  }

  selectPeriod = selectedPeriod => {
    const { periods } = this.props;
    const value = selectedPeriod ? selectedPeriod.value : null;
    const period = periods.find(period => period.name === value);

    this.props.selectPeriodForBatchProvisions(period);
  };

  selectConnection = selectedConnection => {
    const {
      selectConnectionForBatchProvisions,
    } = this.props;

    const value = selectedConnection ? selectedConnection.value : null;

    selectConnectionForBatchProvisions(value);
    this.setState({
      calculateProvisions: false,
      loadTrialBalances: false,
      proformaCalc: false,
    });
  };

  onCheckboxChange = event => {
    const { name, checked } = event.target;

    if (name === 'loadTrialBalances') {
      if (!checked) {
        this.setState({
          calculateProvisions: false,
        });
      }
      this.setState({
        loadTrialBalances: checked,
      });
    } else if (name === 'calculateProvisions') {
      this.setState({
        calculateProvisions: checked,
      });
    }
  };

  onLedgersChange = event => {
    this.setState({
      ledgers: event.target.value,
    });
  };

  createValidationData = () => ({
    userRole: this.props.userRole,
    period: this.props.period,
    currentPeriod: this.props.currentPeriod,
  });

  uploadTrialBalanceFile = event => {
    event.persist();
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const { period, connection } = this.props;
    const { calculateProvisions, loadTrialBalances } = this.state;

    this.props.processBatchProvisions({
      file,
      period,
      connection,
      calculateProvisions,
      loadTrialBalances,
      validationData: this.createValidationData(),
    });

    event.target.value = '';
  };

  calculateProvisionsWithoutUploading = () => {
    const { period, connection } = this.props;
    const { calculateProvisions, loadTrialBalances } = this.state;

    this.props.processBatchProvisions({
      period,
      connection,
      calculateProvisions,
      loadTrialBalances,
      skipFileUpload: true,
    });
  };

  initiateIntegration = () => {
    const { period, connection } = this.props;
    const { ledgers } = this.state;

    this.props.processBatchProvisionsForGlUIConnection({
      period,
      connection,
      ledgers,
    });
  };

  runProformaCalc = () => {
    const { period, connection } = this.props;
    const { proformaCalc } = this.state;

    this.props.processBatchProvisionsForFTPConnection({
      period,
      connection,
      proformaCalc,
    });
  };

  refreshStatus = () => {
    const {
      period,
    } = this.props;
    this.props.refreshStatus({ period });
  };

  changeTab = (event, tabType) => {
    this.setState({
      activeTab: tabType,
    });
  };

  onBatchProvisionJournalClick = () => {
    updateLocalStorageAndPushCustomReport({
      reportSetId: this.props.batchProvisionJournalsReportIds.reportSetId,
      reportId: this.props.batchProvisionJournalsReportIds.reportId,
      history: this.props.history,
      periodName: this.props.period?.name,
      connName: this.props.connection?.connName,
    });
  };

  renderAdditionalDropdownItems = () => (
    <button
      className="hamburger-action link-button"
      tabIndex="0"
      onClick={this.onBatchProvisionJournalClick}
      disabled={false}
      key="batchProvisionJournalReport"
    >
      Batch Provision Journal Report
    </button>
  );

  render() {
    const {
      isUploading,
      isStartingCalc,
      status,
      connection,
      connections,
      processedSuccessfully,
      trialBalancesNotReceived,
      trialBalancesWithErrors,
      calculationsWithErrors,
      otherErrors,
      allProvisions,
      componentToRenderOnSearchBar,
      isSelectedGlUIConnection,
      hasUploadTrialBalancePrivilege,
      hideCannotBeginProcessingModal,
      showCannotBeginProcessingModal,
      cannotBeginProcessingMessageModal,
      period,
      environmentSuffix,
      isLoadingCompanyCodesForConnection,
      companyCodesForConnection,
      isCreatingJob,
      isRefreshingStatus,
      canLoadTrialBalanceAndCalculate,
      isInCurrentPeriod,
    } = this.props;

    const isLoading = isStartingCalc || isUploading;

    const { calculateProvisions, loadTrialBalances, proformaCalc, activeTab, ledgers } = this.state;

    const selectedConnection = connection ? connection.connName : '';

    const { connStatusName, updatedOn, tbLoadStatus, tbBatchID } = status;

    const lastUpdateTime = updatedOn ? renderDate(updatedOn) : null;

    let tbIsLoaded = false;
    if (tbLoadStatus === 1 && tbBatchID !== 0) {
      tbIsLoaded = true;
    }

    return (
      <div className="batch-provisions-page">
        <SecondaryMenu>
          <PeriodDropdown disabled={isLoading} onChange={this.selectPeriod} />
          <Select
            clearable={false}
            disabled={isLoading}
            name="connection"
            options={connections}
            onChange={this.selectConnection}
            scrollMenuIntoView={false}
            value={selectedConnection}
            className="select-connection"
            placeholder="Select a Connection..."
            virtualized
          />
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown
              environmentSuffix={environmentSuffix}
              periodName={period.name}
              isLoadingCompanyCodesForConnection={isLoadingCompanyCodesForConnection}
              connection={this.props.connection}
              companyCodesForConnection={companyCodesForConnection}
              additionalItems={this.renderAdditionalDropdownItems()}
            />
          </div>
        </SecondaryMenu>
        <div className="page">
          <ReduxAlert/>
          <div className="container-fluid page-content-container">
            <div className="row u-border-bottom">
              <div className="col">
                <h5>Batch Provision Processing</h5>
              </div>
            </div>
            {isSelectedGlUIConnection ? (
              <GlUIConnectionForm
                calculateProvisionsForGlUIConnection={this.initiateIntegration}
                onCheckboxChange={this.onCheckboxChange}
                onLedgersChange={this.onLedgersChange}
                refreshStatus={this.refreshStatus}
                calculateProvisions={calculateProvisions}
                connection={connection}
                connStatusName={connStatusName}
                isUploading={isLoading || isCreatingJob}
                lastUpdateTime={lastUpdateTime}
                ledgers={ledgers}
                loadTrialBalances={loadTrialBalances}
                updatedOn={updatedOn}
                isRefreshingStatus={isRefreshingStatus}
                canLoadTrialBalanceAndCalculate={canLoadTrialBalanceAndCalculate}
              />
            ) : (
              <FileUIConnectionForm
                calculateProvisionsWithoutUploading={this.calculateProvisionsWithoutUploading}
                calculateProvisionsForFTPConnection={this.runProformaCalc}
                onCheckboxChange={this.onCheckboxChange}
                refreshStatus={this.refreshStatus}
                uploadTrialBalanceFile={this.uploadTrialBalanceFile}
                calculateProvisions={calculateProvisions}
                connection={connection}
                connStatusName={connStatusName}
                isUploading={isLoading || isCreatingJob}
                lastUpdateTime={lastUpdateTime}
                loadTrialBalances={loadTrialBalances}
                proformaCalc={proformaCalc}
                updatedOn={updatedOn}
                hasUploadPrivilege={hasUploadTrialBalancePrivilege}
                showCannotBeginProcessingModal={showCannotBeginProcessingModal}
                isRefreshingStatus={isRefreshingStatus}
                canLoadTrialBalanceAndCalculate={canLoadTrialBalanceAndCalculate}
                isInCurrentPeriod={isInCurrentPeriod}
                tbIsLoaded={tbIsLoaded}
              />
            )}
            <div className="mt-2">
              <JimJobInfo
                jobConfig={Config.jimJobs.batchProvisionsImport}
                onJobComplete={this.refreshStatus}
                showProgressBar
                progressBarText={'Uploading file'}
              />
            </div>
          </div>

          {connection && (
            <div className="container-fluid page-content-container batch-provisions-grids">
              <Tabs
                defaultValue={TabTypes.ALL_COMPANIES}
                value={activeTab}
                onChange={this.changeTab}
                className="a-tab-bar-margin-10"
                size="md"
              >
                <Tab value={TabTypes.ALL_COMPANIES} label="All Companies">
                  <div className="batch-provisions-grid">
                    <AgGrid
                      columnDefs={allCompaniesColumnDefs}
                      defaultColDef={defaultColumnDefs}
                      rowData={allProvisions}
                      withSearchBar
                    />
                  </div>
                </Tab>

                <Tab value={TabTypes.PROCESSED_SUCCESSFULLY} label="Processed Successfully">
                  <div className="batch-provisions-grid">
                    <AgGrid
                      columnDefs={processedSuccessfullyColumnDefs}
                      defaultColDef={defaultColumnDefs}
                      rowData={processedSuccessfully}
                      withSearchBar
                    />
                  </div>
                </Tab>

                <Tab
                  value={TabTypes.TRIAL_BALANCES_NOT_RECEIVED}
                  label="Trial Balances Not Received"
                >
                  <div className="batch-provisions-grid">
                    <AgGrid
                      columnDefs={trialBalancesNotReceivedColumnDefs}
                      defaultColDef={defaultColumnDefs}
                      rowData={trialBalancesNotReceived}
                      withSearchBar
                    />
                  </div>
                </Tab>

                <Tab value={TabTypes.TRIAL_BALANCES_WITH_ERRORS} label="Trial Balances With Errors">
                  <div className="batch-provisions-grid">
                    <AgGrid
                      columnDefs={trialBalancesValidationErrorsColumnDefs}
                      defaultColDef={defaultColumnDefs}
                      rowData={trialBalancesWithErrors}
                      withSearchBar
                    />
                  </div>
                </Tab>

                <Tab value={TabTypes.CALCULATION_ERRORS} label="Calculation Errors">
                  <div className="batch-provisions-grid">
                    <AgGrid
                      columnDefs={calculationErrorsColumnDefs}
                      defaultColDef={defaultColumnDefs}
                      rowData={calculationsWithErrors}
                      withSearchBar
                    />
                  </div>
                </Tab>

                <Tab value={TabTypes.OTHER_ERRORS} label="Other Errors">
                  <div className="batch-provisions-grid">
                    <AgGrid
                      componentToRenderOnSearchBar={componentToRenderOnSearchBar}
                      columnDefs={otherErrorsColumnDefs}
                      defaultColDef={defaultColumnDefs}
                      rowData={otherErrors}
                      withSearchBar
                    />
                  </div>
                </Tab>

                <Tab value={TabTypes.JOURNALS} label="Journals">
                  <BatchProvisionJournalsPill />
                </Tab>
              </Tabs>
            </div>
          )}
        </div>
        <CannotBeginProcessingMessageModal
          hideModal={hideCannotBeginProcessingModal}
          isVisible={cannotBeginProcessingMessageModal.show}
        />
        <ReactTooltip/>
      </div>
    );
  }
}

export default connect(state => {
  const {
    batchProvisionsWithStatus,
  } = state.provision.batchProvisions;
  const {
    general,
    status,
    processedSuccessfully,
    trialBalancesNotReceived,
    trialBalancesWithErrors,
    calculationsWithErrors,
    otherErrors,
    allProvisions,
  } = batchProvisionsWithStatus;

  const isLastJobOfTypeInProgressSelector = isLastJobOfTypeInProgressSelectorFactory(
    Config.jimJobs.batchProvisionsImport.type
  );

  return {
    period: periodOrCurrentSelector(state),
    periods: periodsSelector(state),
    connections: connectionsOptionsSelector(state),
    connection: connectionSelector(state),
    isSelectedGlUIConnection: isGlUIConnectionSelectedSelector(state),
    isUploading: isLastJobOfTypeInProgressSelector(state),
    isStartingCalc: isStartingCalcBatchProvisionsSelector(state),
    isCreatingJob: isAddingJobOfTypeSelectorFactory(Config.jimJobs.batchProvisionsImport.type)(state),
    status,
    processedSuccessfully,
    trialBalancesNotReceived,
    trialBalancesWithErrors,
    calculationsWithErrors,
    otherErrors,
    allProvisions,
    general,
    hasUploadTrialBalancePrivilege: hasUploadTrialBalancePrivilegeSelector(state),
    cannotBeginProcessingMessageModal: modalDataSelector(state, ModalNames.CannotBeginProcessingMessage),
    currentPeriod: currentPeriodSelector(state),
    userRole: userRoleSelector(state),
    environmentSuffix: environmentSuffixSelector(state),
    isLoadingCompanyCodesForConnection: isLoadingCompanyCodesForConnectionSelector(state),
    companyCodesForConnection: companyCodesForConnectionSelector(state),
    batchProvisionJournalsReportIds: batchProvisionJournalsReportAndReportSetIdsSelector(state),
    isFetchingBatchProvisions: isFetchingBatchProvisionsSelector(state),
    isRefreshingStatus: isRefreshingStatusSelector(state),
    canLoadTrialBalanceAndCalculate: canLoadTrialBalanceAndCalculateSelector(state),
    isInCurrentPeriod: isInCurrentPeriodSelector(state),
  };
}, {
  selectPeriodForBatchProvisions,
  selectConnectionForBatchProvisions,
  fetchBatchProvisions,
  fetchInitialDataForBatchProvisions,
  processBatchProvisions,
  processBatchProvisionsForGlUIConnection,
  processBatchProvisionsForFTPConnection,
  hideCannotBeginProcessingModal,
  showCannotBeginProcessingModal,
  fetchCustomReports,
  errorNotification,
  successNotification,
  refreshStatus,
})(BatchProvisionsPage);
